<script setup lang="ts">
import type { Locale, Money } from '@taler/domain'
import type { ReconciliationRow as ReconciliationRowData } from '../internal/types.ts'
import { computed, reactive } from 'vue'
import { useI18n } from 'vue-i18n'
import type { AccountLookup } from '../internal/account-index.ts'
import ReconciliationRow from '@/features/reconciliation/components/ReconciliationRow.vue'

const props = defineProps<{
  rows: readonly ReconciliationRowData[]
  accounts: ReadonlyMap<string, AccountLookup>
  savingAccountIds: ReadonlySet<string>
  changedAccountIds: ReadonlySet<string>
  lineErrorDetails: ReadonlyMap<string, string>
  disabled: boolean
  locale: Locale
}>()

const emit = defineEmits<{ save: [accountId: string, observedBalance: Money]; clear: [accountId: string] }>()

const { t } = useI18n()

const CLASS_ORDER = ['ASSET', 'LIABILITY'] as const
const classLabels = computed<Record<(typeof CLASS_ORDER)[number], string>>(() => ({
  ASSET: t('reconciliation.editor.assets'),
  LIABILITY: t('reconciliation.editor.liabilities'),
}))

const showHidden = reactive(new Set<string>())
function toggleHidden(group: string): void {
  if (showHidden.has(group)) showHidden.delete(group)
  else showHidden.add(group)
}

const groups = computed(() =>
  CLASS_ORDER.map((accountClass) => {
    const rows = props.rows
      .map((row) => ({ row, account: props.accounts.get(row.accountId) }))
      .filter(
        (entry): entry is { row: ReconciliationRowData; account: AccountLookup } =>
          entry.account !== undefined && entry.account.accountClass === accountClass,
      )
      .sort((a, b) => a.account.code.localeCompare(b.account.code))
    return {
      accountClass,
      label: classLabels.value[accountClass],
      visible: rows.filter((entry) => !entry.account.isHidden),
      hidden: rows.filter((entry) => entry.account.isHidden),
    }
  }).filter((group) => group.visible.length > 0 || group.hidden.length > 0),
)
</script>

<template>
  <table class="w-full border-collapse">
    <template v-for="group in groups" :key="group.accountClass">
      <thead>
        <tr>
          <th colspan="4" class="table-head px-[var(--space-cell)] py-[var(--space-inline)]">
            {{ group.label }}
          </th>
        </tr>
      </thead>
      <tbody>
        <ReconciliationRow
          v-for="entry in group.visible"
          :key="entry.row.accountId"
          :row="entry.row"
          :account="entry.account"
          :is-liability="group.accountClass === 'LIABILITY'"
          :saving="savingAccountIds.has(entry.row.accountId)"
          :changed="changedAccountIds.has(entry.row.accountId)"
          :error-detail="lineErrorDetails.get(entry.row.accountId)"
          :disabled="disabled"
          :locale="locale"
          @save="(accountId, value) => emit('save', accountId, value)"
          @clear="(accountId) => emit('clear', accountId)"
        />
        <tr v-if="group.hidden.length > 0">
          <td colspan="4" class="px-[var(--space-cell)] py-[var(--space-inline)]">
            <button
              type="button"
              class="text-[length:var(--text-step-2)] text-[color:var(--color-accent)] underline"
              @click="toggleHidden(group.accountClass)"
            >
              {{ t('reconciliation.editor.hiddenAccounts', { count: group.hidden.length }) }}
            </button>
          </td>
        </tr>
        <ReconciliationRow
          v-for="entry in showHidden.has(group.accountClass) ? group.hidden : []"
          :key="entry.row.accountId"
          :row="entry.row"
          :account="entry.account"
          :is-liability="group.accountClass === 'LIABILITY'"
          :saving="savingAccountIds.has(entry.row.accountId)"
          :changed="changedAccountIds.has(entry.row.accountId)"
          :error-detail="lineErrorDetails.get(entry.row.accountId)"
          :disabled="disabled"
          :locale="locale"
          @save="(accountId, value) => emit('save', accountId, value)"
          @clear="(accountId) => emit('clear', accountId)"
        />
      </tbody>
    </template>
  </table>
</template>
